import { useMemo, useState } from 'react'
import { Alert, Group, NumberInput, Paper, Select, Stack, Table, Text, Title } from '@mantine/core'
import {
  chooseFor,
  formatClock,
  frameFor,
  fuelStrategy,
  greenPace,
  resolveWindows,
  strategySetup,
  type FuelOption,
  type FuelPlan,
  type RacePlanDoc,
  type Regime,
  type SettingFrom,
} from '@racedash/shared'
import { useAttachedEvents, useEventSessions } from '../dashboard/useDashboardSource'
import { useFuelBurn, useFuelMarks } from '../dashboard/useFuelBurn'

/**
 * The fuel side of a race plan: the rules about fuel stops, the team's
 * numbers, and what they come to — which windows the car has to take fuel in.
 *
 * The burn rate and lap time default to what this event has measured for the
 * car, and either can be overridden: a measured rate that is out for some
 * reason should not be the one the race is run on.
 */
export default function FuelPlanSection({
  plan,
  orgId,
  onChange,
}: {
  plan: RacePlanDoc
  orgId: string | undefined
  onChange: (next: RacePlanDoc) => void
}) {
  const { events } = useAttachedEvents(orgId)
  const event = (events ?? []).find((e) => e.eventId === plan.eventId)
  const cars = useMemo(() => (event?.entries ?? []).map((e) => e.number).filter(Boolean), [event])
  const [picked, setPicked] = useState<string | null>(null)
  const car = picked ?? cars[0] ?? null

  // The team's sessions at the event, for what has been measured so far.
  const { sessions } = useEventSessions(plan.eventId || null)
  const ours = useMemo(
    () =>
      event
        ? chooseFor(event.entries, sessions, { nowMs: Date.now(), otherSeries: event.otherSeries, unresolved: 'nothing' })
            .ours
        : [],
    [event, sessions],
  )
  const { marks } = useFuelMarks(plan.eventId || null, orgId)
  const fuelContext = useMemo(
    () => ({
      eventId: plan.eventId,
      sessions: ours.flatMap((s) => {
        const start = s.clockStartAt?.toDate() ?? s.firstSeenAt?.toDate()
        return start ? [{ id: s.id, start, cars: s.cars ?? [] }] : []
      }),
      marks,
    }),
    [plan.eventId, ours, marks],
  )
  const { burn, laps } = useFuelBurn(plan.eventId ? fuelContext : undefined, car)
  const pace = useMemo(() => greenPace(laps), [laps])

  const windows = useMemo(() => resolveWindows(plan.regime, frameFor(plan)), [plan])
  const rates = burn?.rates
  const setup = useMemo(() => strategySetup(plan, { measured: rates, pace, windows }), [plan, rates, pace, windows])
  const strategy = useMemo(() => (setup.input ? fuelStrategy(setup.input) : null), [setup.input])

  const fuel = plan.fuel ?? {}
  const setFuel = (change: Partial<FuelPlan>) => onChange({ ...plan, fuel: { ...fuel, ...change } })
  const regime = plan.regime
  const setRefuel = (change: Partial<Regime['components']['refuel']>) =>
    onChange({
      ...plan,
      regime: { ...regime, components: { ...regime.components, refuel: { ...regime.components.refuel, ...change } } },
    })
  const setDriverChange = (change: Partial<Regime['components']['driverChange']>) =>
    onChange({
      ...plan,
      regime: {
        ...regime,
        components: { ...regime.components, driverChange: { ...regime.components.driverChange, ...change } },
      },
    })

  return (
    <Stack gap="sm">
      <div>
        <Title order={2} size="h5">
          Fuel
        </Title>
        <Text c="dimmed" size="sm">
          What the rules say about fuel stops, your numbers, and which windows the car has to take
          fuel in. Planned on green running, which is the safe side: a yellow uses less fuel than
          the time it takes.
        </Text>
      </div>

      <Group gap="md" wrap="wrap" align="flex-start">
        <NumberInput
          label="Fuel stops required"
          description="Where the rules set a minimum."
          w={170}
          min={0}
          value={regime.components.refuel.minCount ?? ''}
          onChange={(v) => setRefuel({ minCount: typeof v === 'number' && v > 0 ? v : undefined })}
        />
        <Select
          label="Fuel may go in"
          description="Outside a window, a stop just for fuel."
          w={230}
          allowDeselect={false}
          data={[
            { value: 'anytime', label: 'At any stop' },
            { value: 'windowOnly', label: 'Only in a window' },
          ]}
          value={regime.components.refuel.allowed}
          onChange={(v) => setRefuel({ allowed: v === 'windowOnly' ? 'windowOnly' : 'anytime' })}
        />
        <Seconds
          label="Shortest stop"
          description="Pit in to out, driver change."
          value={regime.components.driverChange.minDurationMs}
          onChange={(ms) => setDriverChange({ minDurationMs: ms })}
        />
        <Seconds
          label="Shortest stop with fuel"
          description="Pit in to out."
          value={regime.components.refuel.minDurationMs}
          onChange={(ms) => setRefuel({ minDurationMs: ms })}
        />
      </Group>

      <Group gap="md" wrap="wrap" align="flex-start">
        <Litres label="Tank" description="What it holds." value={fuel.tankLitres} onChange={(v) => setFuel({ tankLitres: v })} />
        <Litres
          label="Reserve"
          description="Never planned below."
          value={fuel.reserveLitres}
          zero
          onChange={(v) => setFuel({ reserveLitres: v })}
        />
        <Litres
          label="Starts with"
          description="Empty for a full tank."
          value={fuel.startLitres}
          onChange={(v) => setFuel({ startLitres: v })}
        />
      </Group>

      <Group gap="md" wrap="wrap" align="flex-start">
        {cars.length > 1 && (
          <Select
            label="Measured on car"
            w={150}
            allowDeselect={false}
            data={cars}
            value={car}
            onChange={(v) => setPicked(v)}
          />
        )}
        <NumberInput
          label="Burn, green"
          description={measuredNote(burn?.rates.green, 'L/lap', burn?.rates.note)}
          w={190}
          min={0}
          step={0.01}
          decimalScale={3}
          placeholder={burn?.rates.green?.toFixed(2)}
          value={fuel.greenRate ?? ''}
          onChange={(v) => setFuel({ greenRate: typeof v === 'number' && v > 0 ? v : undefined })}
        />
        <NumberInput
          label="Burn, under FCY"
          description={measuredNote(burn?.rates.fcy, 'L/lap')}
          w={190}
          min={0}
          step={0.01}
          decimalScale={3}
          placeholder={burn?.rates.fcy?.toFixed(2)}
          value={fuel.fcyRate ?? ''}
          onChange={(v) => setFuel({ fcyRate: typeof v === 'number' && v > 0 ? v : undefined })}
        />
        <NumberInput
          label="Green lap"
          description={pace != null ? `Measured ${lapTime(pace)}. Seconds.` : 'Seconds. Nothing measured yet.'}
          w={190}
          min={0}
          step={0.1}
          decimalScale={1}
          placeholder={pace != null ? (pace / 1000).toFixed(1) : undefined}
          value={fuel.lapMs != null ? fuel.lapMs / 1000 : ''}
          onChange={(v) => setFuel({ lapMs: typeof v === 'number' && v > 0 ? Math.round(v * 1000) : undefined })}
        />
        <Seconds
          label="Stop, as planned"
          description={`Without fuel. ${fromNote(setup.stopMs.from, setup.stopMs.value)}`}
          value={fuel.stopMs}
          placeholder={setup.stopMs.value}
          onChange={(ms) => setFuel({ stopMs: ms })}
        />
        <Seconds
          label="Fuel stop, as planned"
          description={fromNote(setup.fuelStopMs.from, setup.fuelStopMs.value)}
          value={fuel.fuelStopMs}
          placeholder={setup.fuelStopMs.value}
          onChange={(ms) => setFuel({ fuelStopMs: ms })}
        />
      </Group>

      {setup.input && strategy ? (
        <StrategyTable strategy={strategy} setup={setup} />
      ) : (
        <Text size="sm" c="dimmed">
          For a fuel strategy this needs {setup.missing.join(', ')}.
        </Text>
      )}
    </Stack>
  )
}

function StrategyTable({
  strategy,
  setup,
}: {
  strategy: ReturnType<typeof fuelStrategy>
  setup: ReturnType<typeof strategySetup>
}) {
  const input = setup.input!
  const minutes = (strategy.rangeLaps * input.lapMs) / 60_000
  if (strategy.fewest == null) {
    return (
      <Alert color="red" title="No way to the flag on these numbers">
        A full tank is {strategy.rangeLaps.toFixed(1)} laps, about {Math.round(minutes)} minutes, and the windows are too
        far apart for it{input.anytime ? '' : ' with fuel only in the windows'}.
      </Alert>
    )
  }

  // The best few at each number of stops from the fewest to what the rules
  // want, so "just about with five" sits beside "easily with six".
  const most = Math.max(strategy.fewest, setup.stopsRequired)
  const shown: FuelOption[] = []
  for (let n = strategy.fewest; n <= most; n++) {
    shown.push(...strategy.options.filter((o) => o.stops.length === n).slice(0, 3))
  }

  return (
    <Stack gap="xs">
      <Text size="sm">
        A full tank is <b>{strategy.rangeLaps.toFixed(1)} laps</b>, about {Math.round(minutes)} minutes of green running
        at {lapTime(input.lapMs)} and {input.rate.toFixed(2)} L/lap. Fewest fuel stops in the windows:{' '}
        <b>{strategy.fewest}</b>
        {setup.stopsRequired > strategy.fewest ? `, against ${setup.stopsRequired} required` : ''}.
      </Text>
      <Paper withBorder>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Fuel in windows</Table.Th>
              <Table.Th>Margin</Table.Th>
              <Table.Th>When, where it matters</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {shown.map((o) => (
              <Table.Tr key={o.stops.map((s) => s.window).join(',')}>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>
                  {o.stops.length === 0 ? 'None' : o.stops.map((s) => s.window).join(', ')}
                  {o.splashes > 0 && (
                    <Text component="span" size="xs" c="dimmed">
                      {' '}
                      + {o.splashes} splash{o.splashes === 1 ? '' : 'es'} outside a window
                    </Text>
                  )}
                </Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>
                  <Text size="sm" c={o.marginLaps < 2 ? 'orange' : undefined}>
                    {o.marginLaps.toFixed(1)} laps
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{constraints(o) || 'Any time in each window'}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
      <Text size="xs" c="dimmed">
        Margin is how far the tightest stop can move within its window. A splash is a stop for fuel
        alone — late in the race, and under a yellow if one comes, it costs least.
      </Text>
    </Stack>
  )
}

/** The stops that cannot fall anywhere in their window, and where they must. */
function constraints(o: FuelOption): string {
  return o.stops
    .filter((s) => s.earliest.getTime() > s.opens.getTime() || s.latest.getTime() < s.closes.getTime())
    .map((s) => {
      const late = s.earliest.getTime() > s.opens.getTime()
      const early = s.latest.getTime() < s.closes.getTime()
      const when =
        late && early
          ? `${formatClock(s.earliest)}–${formatClock(s.latest)}`
          : late
            ? `after ${formatClock(s.earliest)}`
            : `by ${formatClock(s.latest)}`
      return `W${s.window} ${when}`
    })
    .join(' · ')
}

function measuredNote(v: number | null | undefined, unit: string, note?: string | null): string {
  if (v == null) return note ? `${note}.` : 'Nothing measured yet.'
  return `Measured ${v.toFixed(2)} ${unit}. Empty to use it.`
}

function fromNote(from: SettingFrom, ms: number): string {
  const s = `${Math.round(ms / 1000)} s`
  return from === 'rules' ? `${s} from the rules.` : from === 'plan' ? '' : `${s} unless set.`
}

function lapTime(ms: number): string {
  const s = ms / 1000
  return `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, '0')}`
}

function Litres({
  label,
  description,
  value,
  zero,
  onChange,
}: {
  label: string
  description: string
  value: number | undefined
  /** Whether nothing is a real answer, as a reserve can be. */
  zero?: boolean
  onChange: (v: number | undefined) => void
}) {
  return (
    <NumberInput
      label={label}
      description={description}
      w={150}
      min={0}
      decimalScale={1}
      suffix=" L"
      value={value ?? ''}
      onChange={(v) => onChange(typeof v === 'number' && (v > 0 || (zero && v === 0)) ? v : undefined)}
    />
  )
}

function Seconds({
  label,
  description,
  value,
  placeholder,
  onChange,
}: {
  label: string
  description: string
  value: number | undefined
  placeholder?: number
  onChange: (ms: number | undefined) => void
}) {
  return (
    <NumberInput
      label={label}
      description={description}
      w={190}
      min={0}
      suffix=" s"
      placeholder={placeholder != null ? `${Math.round(placeholder / 1000)} s` : undefined}
      value={value != null ? value / 1000 : ''}
      onChange={(v) => onChange(typeof v === 'number' && v > 0 ? Math.round(v * 1000) : undefined)}
    />
  )
}
